/**
 * Ephemeral overlay store for Stage H — remote cursors and in-progress
 * freehand strokes from other connections in the same session.
 *
 * This state is explicitly NOT persisted and never touches the main scene
 * store. Cursors and live strokes are fire-and-forget broadcasts; the
 * store expires entries on a ~1s sweep so a silent player disappears from
 * the canvas without the server having to send anything.
 *
 * A single module-level instance is shared by SceneClient (writer) and
 * SceneCanvas (reader + subscriber for redraw). Keeping it outside the
 * main scene store means a pointer-move at 120 Hz can't accidentally
 * trigger scene-element diffing or undo bookkeeping.
 */

/** How long after its last update a cursor stays visible. */
const CURSOR_TTL_MS = 5000;
/** How long a live stroke lingers after its last update. The committed
 *  element arrives via scene/op shortly after the user lifts the pointer —
 *  a short window is enough for the final batch to be overtaken by the
 *  persistent render without a visible gap. */
const STROKE_TTL_MS = 2000;

export interface RemoteCursor {
  userId: string;
  username?: string;
  /** Position in MAP coordinates (not screen). Converted at draw time. */
  x: number;
  y: number;
  updatedAt: number;
}

export interface RemoteStroke {
  elementId: string;
  userId: string;
  /** Absolute map-coordinate pairs, flat [x0, y0, x1, y1, ...]. */
  points: number[];
  pressures?: number[];
  strokeColor?: string;
  strokeWidth?: number;
  updatedAt: number;
}

type Listener = () => void;

class LiveOverlayStore {
  private cursors = new Map<string, RemoteCursor>();
  private strokes = new Map<string, RemoteStroke>();
  private listeners = new Set<Listener>();
  private sweepTimer: ReturnType<typeof setInterval> | null = null;

  getCursors(): RemoteCursor[] {
    return [...this.cursors.values()];
  }

  getStrokes(): RemoteStroke[] {
    return [...this.strokes.values()];
  }

  setCursor(c: RemoteCursor): void {
    this.cursors.set(c.userId, c);
    this.ensureSweep();
    this.notify();
  }

  setStroke(s: RemoteStroke): void {
    this.strokes.set(s.elementId, s);
    this.ensureSweep();
    this.notify();
  }

  clearStroke(elementId: string): void {
    if (this.strokes.delete(elementId)) this.notify();
  }

  /** Drop everything for a user — used when the session resets. */
  reset(): void {
    const had = this.cursors.size > 0 || this.strokes.size > 0;
    this.cursors.clear();
    this.strokes.clear();
    if (had) this.notify();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
      if (this.listeners.size === 0 && this.sweepTimer !== null) {
        clearInterval(this.sweepTimer);
        this.sweepTimer = null;
      }
    };
  }

  private notify(): void {
    for (const l of [...this.listeners]) l();
  }

  private ensureSweep(): void {
    if (this.sweepTimer !== null) return;
    // Sweep once a second — cheap, and guarantees expired cursors leave
    // the canvas even if no other events arrive to drive a redraw.
    this.sweepTimer = setInterval(() => this.sweep(), 1000);
  }

  private sweep(): void {
    const now = Date.now();
    let changed = false;
    for (const [id, c] of this.cursors) {
      if (now - c.updatedAt > CURSOR_TTL_MS) {
        this.cursors.delete(id);
        changed = true;
      }
    }
    for (const [id, s] of this.strokes) {
      if (now - s.updatedAt > STROKE_TTL_MS) {
        this.strokes.delete(id);
        changed = true;
      }
    }
    if (changed) this.notify();
    if (this.cursors.size === 0 && this.strokes.size === 0 && this.sweepTimer !== null) {
      clearInterval(this.sweepTimer);
      this.sweepTimer = null;
    }
  }
}

export const liveOverlay = new LiveOverlayStore();

/**
 * Deterministic accent colour per user id, so each player's cursor and
 * live stroke pick the same hue every time without the server needing to
 * hand out assignments. Biased toward the VTT's amber/teal palette.
 */
const LIVE_PALETTE = [
  "#f5c77a", // amber
  "#6ac1c4", // teal
  "#c79af5", // lilac
  "#a8c27a", // sage
  "#f59e7a", // coral
  "#7ab6f5", // sky
];

export function colourForUser(userId: string): string {
  let h = 0;
  for (let i = 0; i < userId.length; i++) {
    h = (h * 31 + userId.charCodeAt(i)) >>> 0;
  }
  return LIVE_PALETTE[h % LIVE_PALETTE.length];
}
